import { Button } from "@repo/ui/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandList } from "@repo/ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { cn } from "@repo/ui/lib/utils";
import { ChevronsUpDown, Loader2, X } from "lucide-react";
import { createContext, type ReactNode, use, useState } from "react";

interface SearchPickerContextValue {
  state: { open: boolean; query: string };
  actions: { setQuery: (query: string) => void; close: () => void };
  meta: { placeholder: string };
}

const SearchPickerContext = createContext<SearchPickerContextValue | null>(null);

function useSearchPicker() {
  const context = use(SearchPickerContext);
  if (!context) throw new Error("SearchPicker components must be inside SearchPicker.Root");
  return context;
}

function Root({ children, placeholder, onClose }: { children: ReactNode; placeholder: string; onClose?: () => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const close = () => {
    setOpen(false);
    setQuery("");
    onClose?.();
  };

  return (
    <SearchPickerContext value={{ state: { open, query }, actions: { setQuery, close }, meta: { placeholder } }}>
      <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        {children}
      </Popover>
    </SearchPickerContext>
  );
}

function Trigger({
  label,
  onClear,
  clearLabel,
  disabled,
}: {
  label?: string;
  onClear?: () => void;
  clearLabel: string;
  disabled?: boolean;
}) {
  const { meta } = useSearchPicker();
  return (
    <div className="relative">
      <PopoverTrigger
        disabled={disabled}
        render={
          <Button
            type="button"
            variant="outline"
            className={cn("w-full justify-between text-sm font-normal h-9", onClear && !disabled && "pr-9")}
          />
        }
      >
        {label ? (
          <span className="truncate">{label}</span>
        ) : (
          <span className="text-muted-foreground">{meta.placeholder}</span>
        )}
        {!disabled && !onClear && <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />}
      </PopoverTrigger>
      {/* A sibling avoids the trigger's press handler opening the picker before clear runs. */}
      {onClear && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={clearLabel}
          className="absolute top-1/2 right-1.5 -translate-y-1/2 opacity-50 hover:opacity-100"
          onClick={onClear}
        >
          <X className="size-3.5" />
        </Button>
      )}
    </div>
  );
}

function Content({ children }: { children: ReactNode }) {
  return (
    <PopoverContent className="w-[var(--anchor-width)] p-0" align="start">
      {children}
    </PopoverContent>
  );
}

function Search({ children }: { children: ReactNode }) {
  const { state, actions, meta } = useSearchPicker();
  return (
    <Command shouldFilter={false}>
      <CommandInput placeholder={meta.placeholder} value={state.query} onValueChange={actions.setQuery} />
      {/* Keep the footer inside cmdk's list for keyboard navigation, but outside the masked scroller. */}
      <CommandList className="max-h-none overflow-visible mask-none!">{children}</CommandList>
    </Command>
  );
}

function Results({
  children,
  isLoading,
  count,
  emptyMessage,
}: {
  children: ReactNode;
  isLoading: boolean;
  count: number;
  emptyMessage: string;
}) {
  const { state } = useSearchPicker();
  return (
    <div className="scroll-fade scroll-fade-12 scrollbar-none max-h-64 overflow-y-auto">
      {isLoading ? (
        <output className="flex items-center justify-center py-4" aria-label="Searching">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </output>
      ) : state.query.length < 2 ? (
        <div className="py-4 text-center text-xs text-muted-foreground">Type at least 2 characters to search</div>
      ) : count === 0 ? (
        <div className="py-4 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      ) : (
        children
      )}
    </div>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return <CommandGroup className="-mx-1 border-t px-2">{children}</CommandGroup>;
}

export const SearchPicker = { Root, Trigger, Content, Search, Results, Footer, useContext: useSearchPicker };
